export const SUPPORTED_LOCALES = ['en', 'tr', 'ko', 'ja', 'th', 'ms'];
export const DEFAULT_LOCALE = 'en';
export const LOCALE_STORAGE_KEY = 'sevenloves.preferredLocale';

export const ROUTES = {
  home: '/',
  download: '/download',
  privacy: '/privacy',
  terms: '/terms',
  smsOptIn: '/sms-optin',
  communityGuidelines: '/community-guidelines',
  dateSafety: '/date-safety',
  deleteAccount: '/delete-account',
};

export function normalizeLocale(value) {
  if (!value) {
    return null;
  }

  const normalized = String(value).trim().toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LOCALES.includes(normalized) ? normalized : null;
}

export function parseLanguageTag(value) {
  if (!value) {
    return null;
  }

  const normalizedTag = String(value).trim().replace(/_/g, '-');
  const segments = normalizedTag.split('-').filter(Boolean);

  if (segments.length === 0) {
    return null;
  }

  const language = /^[a-zA-Z]{2,3}$/.test(segments[0]) ? segments[0].toLowerCase() : null;
  const regionSegment = segments.find((segment, index) => index > 0 && /^[a-zA-Z]{2}$/.test(segment));
  const region = regionSegment ? regionSegment.toUpperCase() : null;

  return {
    tag: normalizedTag,
    language,
    locale: normalizeLocale(language),
    region,
  };
}

export function getBrowserLanguageCandidates() {
  if (typeof window === 'undefined') {
    return [];
  }

  return [
    ...(window.navigator.languages || []),
    window.navigator.language,
    typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().locale : null,
  ].filter(Boolean);
}

export function getSavedLocale() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return normalizeLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch (error) {
    return null;
  }
}

export function detectBrowserLocale() {
  for (const candidate of getBrowserLanguageCandidates()) {
    const parsed = parseLanguageTag(candidate);
    if (parsed?.locale) {
      return parsed.locale;
    }
  }

  return null;
}

export function detectBrowserPreference() {
  for (const candidate of getBrowserLanguageCandidates()) {
    const parsed = parseLanguageTag(candidate);
    if (parsed?.language || parsed?.region) {
      return parsed;
    }
  }

  return null;
}

export function getPreferredLocale() {
  return getSavedLocale() || detectBrowserLocale() || DEFAULT_LOCALE;
}

export function persistLocale(locale) {
  const normalizedLocale = normalizeLocale(locale) || DEFAULT_LOCALE;

  if (typeof window === 'undefined') {
    return normalizedLocale;
  }

  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, normalizedLocale);
  } catch (error) {
    return normalizedLocale;
  }

  return normalizedLocale;
}

export function stripLocaleFromPath(pathname = '/') {
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const segments = normalizedPath.split('/').filter(Boolean);

  if (segments.length === 0) {
    return '/';
  }

  const hasLocalePrefix = Boolean(normalizeLocale(segments[0]));
  const remainingSegments = hasLocalePrefix ? segments.slice(1) : segments;

  if (remainingSegments.length === 0) {
    return '/';
  }

  return `/${remainingSegments.join('/')}`;
}

export function buildLocalizedPath(locale, path = '/') {
  const normalizedLocale = normalizeLocale(locale) || DEFAULT_LOCALE;
  const normalizedPath = (path || '/').startsWith('/') ? (path || '/') : `/${path}`;

  if (normalizedPath === '/') {
    return `/${normalizedLocale}`;
  }

  return `/${normalizedLocale}${normalizedPath.replace(/\/{2,}/g, '/')}`;
}
